import { CountryOption } from '../types';

// Default Tailwind class names for consistent styling
// BEM class names should be added at the end of className strings in components
export const DEFAULT_STYLES = {
  // Field block - main container
  field: 'w-full flex flex-col',
  fieldHidden: 'hidden',

  // Field label element
  field__label: 'block text-sm font-medium text-slateText mb-1',
  field__labelRequired: 'text-brickRed ml-0.5',
  field__labelDisabled: 'text-cement',

  // Field input container element
  field__inputContainer:
    'flex items-center gap-2 px-4 py-2 border rounded-lg bg-white transition-all duration-200 input-container-focused',
  field__inputContainerDefault:
    'border-lightBlueGray300 focus-within:border-focusBorder focus-within:ring-2 focus-within:ring-focusBorder focus-within:ring-opacity-50 input-container-focused',
  field__inputContainerError: 'border-errorBorder',
  field__inputContainerSuccess:
    'border-successBorder focus-within:border-successBorder focus-within:ring-2 focus-within:ring-successBorder focus-within:ring-opacity-50 input-container-focused',
  field__inputContainerDisabled: 'bg-catskillWhite1 cursor-not-allowed',

  // Field input element
  field__input: 'flex-1 text-sm bg-transparent outline-none',
  field__inputText: 'text-inputText', // Typing state #020617
  field__inputPlaceholder: 'placeholder-placeholderText', // Placeholder color #64748b
  field__inputDisabled: 'text-gullGray cursor-not-allowed opacity-100 [&:disabled]:text-gullGray', // Disabled text #94A3B8

  // Field message element (error/helper/success)
  field__message: 'text-xs h-4 mt-0.5 leading-tight',
  field__messageContainer: 'h-5 mt-1 flex items-start',

  // Input styles (legacy - not used in BaseTextField, kept for reference)
  input:
    'w-full px-4 py-2 border rounded-lg text-sm text-slateText bg-white transition-colors duration-200',
  inputDefault:
    'border-lightBlueGray300 focus:border-lightIndigo600 focus:ring-2 focus:ring-lightIndigo600 focus:ring-opacity-50',
  inputError:
    'border-brickRed focus:border-brickRed focus:ring-2 focus:ring-brickRed focus:ring-opacity-50',
  inputDisabled:
    'bg-catskillWhite1 text-cement cursor-not-allowed border-lightBlueGray300',

  // Button block
  button:
    'flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus-visible:outline-none gap-2',
  buttonPrimary:
    'bg-royalBlue text-white hover:bg-darkCerulean focus:shadow-focus-ring-blue focus-visible:shadow-focus-ring-blue active:shadow-focus-ring-blue',
  buttonSecondary:
    'bg-catskillWhite1 text-slateText hover:bg-catskillWhite focus:shadow-focus-ring-blue focus-visible:shadow-focus-ring-blue active:shadow-focus-ring-blue',
  buttonOutline:
    'border border-royalBlue text-royalBlue hover:bg-zumthor focus:shadow-focus-ring-blue focus-visible:shadow-focus-ring-blue active:shadow-focus-ring-blue',
  buttonGhost:
    'text-royalBlue hover:bg-zumthor focus:shadow-focus-ring-blue focus-visible:shadow-focus-ring-blue active:shadow-focus-ring-blue',
  buttonDisabled: 'opacity-50 cursor-not-allowed',
  buttonLoading: 'opacity-75 cursor-wait',
  buttonSizeSm: 'px-3 py-2 text-sm',
  buttonSizeMd: 'px-6 py-2 text-sm',
  buttonSizeLg: 'px-6 py-3 text-base',
  buttonFullWidth: 'w-full',
  // Dropdown/Bottom sheet primary button variant
  buttonDropdownPrimary:
    'border border-lightIndigo600 text-lightIndigo600 bg-white hover:bg-bottomSheetOptionBg focus:shadow-focus-ring-indigo focus-visible:shadow-focus-ring-indigo active:shadow-focus-ring-indigo border-lightIndigo600',

  // Dropdown block
  dropdown:
    'absolute z-50 w-full mt-1 bg-white border border-lightBlueGray300 rounded-lg shadow-lg max-h-60 overflow-auto',
  dropdown__trigger:
    'w-full px-4 py-2 border rounded-lg text-sm text-slateText bg-white flex items-center justify-between cursor-pointer transition-colors duration-200',
  dropdown__triggerOpen:
    'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50',
  dropdown__option:
    'p-2 rounded-lg text-sm text-slateText cursor-pointer hover:bg-bottomSheetOptionBg transition-colors duration-150',
  dropdown__optionSelected: 'bg-bottomSheetOptionBg text-slateText',
  dropdown__optionDisabled: 'text-cement cursor-not-allowed hover:bg-white',
  dropdown__searchInput:
    'w-full px-4 py-2 border-b border-lightBlueGray300 text-sm outline-none',
  dropdown__noResults: 'px-4 py-3 text-sm text-cement text-left',
  dropdown__loading: 'px-4 py-3 text-sm text-mirage text-left',

  // Radio block
  radio: 'flex items-center gap-2 cursor-pointer',
  radio__input:
    'w-4 h-4 text-royalBlue border-lightBlueGray300 focus:ring-2 focus:ring-lightIndigo600 focus:ring-opacity-50',
  radio__label: 'text-sm text-slateText',
  radioDisabled: 'opacity-50 cursor-not-allowed',
  radioHorizontal: 'flex flex-row flex-wrap gap-4',
  radioVertical: 'flex flex-col gap-2',

  // Checkbox block
  checkbox: 'flex items-start gap-2 cursor-pointer group',
  checkbox__input: 'sr-only', // Screen reader only - we use custom checkbox visual
  checkbox__visual:
    'flex items-center justify-center rounded flex-shrink-0 transition-colors duration-200 w-4 h-4 p-0.5',
  checkbox__visualUnchecked:
    'bg-white border border-lightBlueGray300 group-hover:border-gullGray group-focus-within:border-lightBlueGray300 group-focus-within:shadow-focus-ring-gray',
  checkbox__visualChecked:
    'bg-lightIndigo600 border-0 group-hover:bg-lightIndigo700 group-focus-within:bg-lightIndigo600 group-focus-within:shadow-focus-ring-indigo-light',
  checkbox__visualUncheckedDisabled:
    'bg-catskillWhite1 border border-lightBlueGray300',
  checkbox__visualCheckedDisabled: 'bg-lightIndigo300 border-0',
  checkbox__label:
    'text-sm font-medium leading-5 tracking-tight text-darkLightBlueGray',
  checkbox__labelChecked: 'text-lightblueGray800',
  checkbox__labelDisabled: 'text-lightBlueGray300',
  checkboxDisabled: 'opacity-50 cursor-not-allowed',

  // Side element (used in field input container)
  field__sideElement: 'flex-shrink-0 text-cement flex',
  field__sideElementClickable:
    'flex-shrink-0 text-cement hover:text-slateText transition-colors cursor-pointer',

  // Bottom sheet block
  bottomSheet:
    'relative w-full bg-white rounded-t-2xl shadow-2xl flex flex-col z-10',
  bottomSheet__overlay: 'fixed inset-0 z-50 flex items-end',
  bottomSheet__backdrop:
    'absolute inset-0 bg-black transition-opacity duration-300',
  bottomSheet__content: 'flex-1 overflow-y-auto overflow-x-hidden flex flex-col',
  bottomSheet__header:
    'flex items-center justify-between px-4 py-4 pb-2 flex-shrink-0',
  bottomSheet__title: 'text-lg font-semibold text-slateText',
  bottomSheet__closeButton:
    'ml-auto flex text-cement hover:text-slateText transition-colors my-auto',
  bottomSheet__option:
    'p-2 rounded-lg text-sm text-slateText cursor-pointer hover:bg-bottomSheetOptionBg transition-colors duration-150',
  bottomSheet__optionSelected: 'bg-bottomSheetOptionBg text-slateText',
  bottomSheet__optionDisabled: 'text-cement cursor-not-allowed hover:bg-white',

  // Select bottom sheet block
  select__bottomSheet: '',
  select__bottomSheet__option:
    'p-2 rounded-lg text-sm text-slateText cursor-pointer hover:bg-bottomSheetOptionBg transition-colors duration-150',
  select__bottomSheet__optionSelected: 'bg-bottomSheetOptionBg text-slateText',
  select__bottomSheet__optionDisabled:
    'text-cement cursor-not-allowed hover:bg-white',

  // Select dropdown block
  select__dropdown__trigger:
    'w-full px-4 py-2 border rounded-lg text-sm text-slateText bg-white flex items-center justify-between cursor-pointer transition-colors duration-200',
  select__dropdown__triggerOpen:
    'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50 select-container-focused',

  // ComboBox bottom sheet block
  combobox__bottomSheet: '',
  combobox__bottomSheet__option:
    'p-2 rounded-lg text-sm text-slateText cursor-pointer hover:bg-bottomSheetOptionBg transition-colors duration-150',
  combobox__bottomSheet__optionSelected:
    'bg-bottomSheetOptionBg text-slateText',
  combobox__bottomSheet__optionDisabled:
    'text-cement cursor-not-allowed hover:bg-white',
  combobox__bottomSheet__button:
    'border border-lightIndigo600 text-lightIndigo600 bg-white hover:bg-bottomSheetOptionBg focus:shadow-focus-ring-indigo focus-visible:shadow-focus-ring-indigo active:shadow-focus-ring-indigo border-lightIndigo600',

  // ComboBox dropdown block
  combobox__dropdown__button:
    'border border-lightIndigo600 text-lightIndigo600 bg-white hover:bg-bottomSheetOptionBg focus:shadow-focus-ring-indigo focus-visible:shadow-focus-ring-indigo active:shadow-focus-ring-indigo border-lightIndigo600'
} as const;

// Common country list with dial codes
export const COUNTRIES: CountryOption[] = [
  { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' }
];

// Default country code
export const DEFAULT_COUNTRY_CODE = 'IN';
export const DEFAULT_DIAL_CODE = '+91';

// Keyboard keys for navigation
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  ESCAPE: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  TAB: 'Tab',
  SPACE: ' '
} as const;

// Debounce delay for search
export const SEARCH_DEBOUNCE_MS = 300;

// Miscellaneous constants
export { STRING_DELIMITER } from './MiscellaneousConstants';
export { DAY_WISE, WEEK_WISE } from './SlotConstants';
export {
  TODAY,
  TOMORROW,
  TIME_LAPSED,
  DAY_LAPSED
} from './TimeLapsedConstants';
export {
  branchOptions,
  degreeBranchOptions,
  engineeringBranchOptions
} from './BranchConstants';
export { radioGroupTypes } from './RadioGroupConstants';

// Regex patterns
export {
  allowedCharactersInComboBoxRegexPattern,
  allowedCharactersInTextFieldRegexPattern
} from './RegexPatternConstants';
